import React, { useState, useEffect } from 'react'
import { Container, Typography, Box, CssBaseline, ThemeProvider, createTheme, Alert, Paper } from '@mui/material'
import AudioInput from './components/AudioInput'
import TranscriptionResults from './components/TranscriptionResults'
import ModelSelector, { availableModels } from './components/ModelSelector'
import { STTManager, STTConfig, ModelConfig } from './services/stt'

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#60A5FA',
      light: '#93C5FD',
      dark: '#2563EB',
    },
    secondary: {
      main: '#F472B6',
      light: '#F9A8D4',
      dark: '#DB2777',
    },
    background: {
      default: '#111827',
      paper: '#1F2937',
    },
    text: {
      primary: '#F3F4F6',
      secondary: '#9CA3AF',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
      marginBottom: '1rem',
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    body1: {
      lineHeight: 1.7,
    },
    body2: {
      lineHeight: 1.6,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
            transition: 'transform 0.2s',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          height: 32,
        },
      },
    },
  },
  shape: {
    borderRadius: 16,
  },
  spacing: 8,
})

const App: React.FC = () => {
  const [currentAudio, setCurrentAudio] = useState<Blob | null>(null)
  const [selectedModels, setSelectedModels] = useState<ModelConfig[]>(() => 
    availableModels.map(model => ({ ...model, isEnabled: true }))
  )
  const [results, setResults] = useState<any[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sttManager, setSttManager] = useState<STTManager | null>(null)
  const [initError, setInitError] = useState<string | null>(null)

  useEffect(() => {
    try {
      // Log environment variables availability (not their values for security)
      console.log('Checking API keys availability:', {
        openai: !!import.meta.env.VITE_OPENAI_API_KEY,
        elevenLabs: !!import.meta.env.VITE_ELEVENLABS_API_KEY,
        deepgram: !!import.meta.env.VITE_DEEPGRAM_API_KEY,
        google: !!import.meta.env.VITE_GOOGLE_API_KEY,
        anthropic: !!import.meta.env.VITE_ANTHROPIC_API_KEY,
      });

      const openaiApiKey = import.meta.env.VITE_OPENAI_API_KEY;
      const elevenLabsApiKey = import.meta.env.VITE_ELEVENLABS_API_KEY;
      const deepgramApiKey = import.meta.env.VITE_DEEPGRAM_API_KEY;
      const googleApiKey = import.meta.env.VITE_GOOGLE_API_KEY;
      const anthropicApiKey = import.meta.env.VITE_ANTHROPIC_API_KEY;

      // Initialize STT services
      const sttConfig: STTConfig = {
        openaiApiKey,
        elevenLabsApiKey,
        deepgramApiKey,
        googleApiKey,
        anthropicApiKey,
      };

      console.log('Initializing STT Manager...');
      const manager = new STTManager(sttConfig);
      console.log('STT Manager initialized successfully');
      
      setSttManager(manager);
      setInitError(null);
    } catch (err) {
      console.error('Failed to initialize STT services:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize services';
      console.error('Detailed error:', errorMessage);
      setInitError(errorMessage);
      setSttManager(null);
    }
  }, []);

  const handleAudioData = async (audioBlob: Blob) => {
    setCurrentAudio(audioBlob)
    setIsProcessing(true)
    setError(null)
    setResults([])

    const startTime = performance.now()
    const enabledModels = selectedModels.filter(model => model.isEnabled)
    const enabledTranslationModels = enabledModels.filter(model => model.type === 'translate')
    console.log('Enabled translation models:', enabledTranslationModels)
    console.log(`Processing ${enabledModels.length} models (${enabledTranslationModels.length} translation models)...`)

    try {
      if (!sttManager) {
        throw new Error('STT services not initialized. Please check your API keys.')
      }

      // Process all models in parallel with timing
      const resultPromises = enabledModels.map(async model => {
        try {
          const modelStartTime = performance.now()
          const result = await sttManager.processAudio(audioBlob, model)
          const modelDuration = (performance.now() - modelStartTime) / 1000
          console.log(`Result for ${model.name}:`, { ...result, actualDuration: modelDuration })
          return { ...result, actualDuration: modelDuration }
        } catch (error) {
          console.error(`Error processing ${model.name}:`, error)
          return {
            model: model.name,
            text: error instanceof Error ? error.message : "Unknown error occurred",
            type: model.type,
            provider: model.provider,
            error: true,
            actualDuration: 0,
            isTextTranslation: false
          }
        }
      })

      const results = await Promise.all(resultPromises)
      const totalDuration = (performance.now() - startTime) / 1000
      
      // Log timing summary in a clean table
      console.table(
        results.map(result => ({
          Model: result.model,
          Type: result.type,
          IsTextTranslation: result.isTextTranslation,
          'Processing Time': result.actualDuration.toFixed(2) + 's'
        }))
      )
      console.log(`Total time: ${totalDuration.toFixed(2)}s`)
      console.log('Final results:', results)

      // Update results even if some models failed
      setResults(results)
    } catch (error) {
      console.error('Error processing audio:', error)
      setError(error instanceof Error ? error.message : 'Error processing audio. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleModelsChange = (newModels: ModelConfig[]) => {
    setSelectedModels(newModels)
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ 
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
      }}>
        <Container maxWidth="md">
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4,
              mb: 4,
              textAlign: 'center',
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              color: 'white',
            }}
          >
            <Typography variant="h4" component="h1" gutterBottom>
              Speech-to-Text Model Comparison
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Record audio or upload a file to compare different STT models
            </Typography>
          </Paper>
          
          {initError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Initialization Error: {initError}
            </Alert>
          )}
          
          <AudioInput 
            onAudioData={handleAudioData} 
            isProcessing={isProcessing} 
            disabled={!sttManager || !!initError}
          />
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <ModelSelector
            selectedModels={selectedModels}
            onModelsChange={handleModelsChange}
            disabled={!sttManager || !!initError}
          />
          
          <TranscriptionResults 
            results={results} 
            setResults={setResults}
            onClear={() => setResults([])}
            isLoading={isProcessing} 
            audioBlob={currentAudio || undefined}
          />
        </Container>
      </Box>
    </ThemeProvider>
  )
}

export default App

